import { Body, Button, Caption, I, Icon, Row, Stack, Thumbnail } from '@leanin/ui';

import type { StagedUpload } from './use-staged-uploads';

// One staged attachment in a composer tray: preview (or file glyph), name, a
// status line (progress, size, or the error), and a remove control.
export function StagedUploadChip({
  upload,
  detail,
  previewAlt,
  removeLabel,
  onRemove,
}: {
  upload: StagedUpload<unknown>;
  /** Status line for the uploading / ready states; the error state shows its own message. */
  detail: string;
  /** Empty when the visible file name already identifies the preview. */
  previewAlt: string;
  removeLabel: string;
  onRemove: () => void;
}) {
  const isError = upload.state.status === 'error';
  return (
    <Row
      gap="sm"
      align="center"
      px="sm"
      py="xs"
      style={{
        border: `1px solid ${isError ? 'var(--destructive)' : 'var(--border)'}`,
        borderRadius: 'var(--radius-md)',
        background: 'var(--card)',
        maxWidth: 280,
      }}
    >
      {upload.previewUrl !== null ? (
        <Thumbnail src={upload.previewUrl} alt={previewAlt} size={32} radius="var(--radius-sm)" />
      ) : (
        <Icon size={16} color="var(--muted-foreground)">
          {I.attach}
        </Icon>
      )}
      <Stack gap="none" style={{ minWidth: 0 }}>
        <Body
          size="sm"
          style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {upload.fileName}
        </Body>
        {upload.state.status === 'error' ? (
          <span role="alert">
            <Body size="sm" color="destructive">
              {upload.state.errorMessage}
            </Body>
          </span>
        ) : (
          <Caption>{detail}</Caption>
        )}
      </Stack>
      <Button variant="ghost" size="sm" icon={I.x} aria-label={removeLabel} onClick={onRemove} />
    </Row>
  );
}
